// Main App Component with Comprehensive Routing

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@components/ProtectedRoute';
import AdminLayout from '@layouts/AdminLayout';
import ClientLayout from '@layouts/ClientLayout';

// Auth Pages
import LoginPage from '@pages/auth/LoginPage';
import RegisterPage from '@pages/auth/RegisterPage';
import AdminLoginPage from '@pages/auth/AdminLoginPage';

// Client Pages
import ClientDashboard from '@pages/client/DashboardPage';
import CartPage from '@pages/client/cart/CartPage';
import CheckoutPage from '@pages/client/checkout/CheckoutPage';
import OrderSuccessPage from '@pages/client/orders/OrderSuccessPage';
import ClientOrdersListPage from '@pages/client/orders/OrdersListPage';
import ClientOrderDetailPage from '@pages/client/orders/OrderDetailPage';
import ClientAnnouncementsPage from '@pages/client/Announcements';
import ClientDownloadsPage from '@pages/client/Downloads';
import ClientServicesPage from '@pages/client/ServicesPage';
import ClientInvoicesPage from '@pages/client/InvoicesPage';
import ClientTicketsPage from '@pages/client/TicketsPage';
import ClientDomainsPage from '@pages/client/DomainsPage';

// Admin Pages
import AdminDashboardPage from '@pages/admin/DashboardPage';
import AdminOrdersListPage from '@pages/admin/orders/OrdersListPage';
import AdminOrderDetailPage from '@pages/admin/orders/OrderDetailPage';
import ClientsListPage from '@pages/admin/clients/ClientsListPage';
import ClientDetailPage from '@pages/admin/clients/ClientDetailPage';
import ProductsListPage from '@pages/admin/products/ProductsListPage';
import WebhooksPage from '@pages/admin/WebhooksPage';
import PromotionsPage from '@pages/admin/Promotions';
import AnalyticsDashboardPage from '@pages/admin/AnalyticsDashboard';
import FinancialReportsPage from '@pages/admin/FinancialReports';
import CustomerAnalyticsPage from '@pages/admin/CustomerAnalytics';
import AdminKnowledgeBasePage from '@pages/admin/KnowledgeBase';
import AdminAnnouncementsPage from '@pages/admin/Announcements';
import AdminDownloadsPage from '@pages/admin/Downloads';
import AdminNetworkStatusPage from '@pages/admin/NetworkStatus';
import LanguagesPage from '@pages/admin/Languages';
import CurrenciesPage from '@pages/admin/Currencies';
import TaxSettingsPage from '@pages/admin/TaxSettings';
import AdminInvoicesPage from '@pages/admin/InvoicesPage';
import AdminServicesPage from '@pages/admin/ServicesPage';
import AdminTicketsPage from '@pages/admin/TicketsPage';
import AdminTicketDepartmentsPage from '@pages/admin/TicketDepartmentsPage';
import AdminCannedResponsesPage from '@pages/admin/CannedResponsesPage';
import AdminDomainsPage from '@pages/admin/DomainsPage';
import AdminEmailTemplatesPage from '@pages/admin/EmailTemplatesPage';
import ClientGroupsPage from '@pages/admin/ClientGroupsPage';
import ProductGroupsPage from '@pages/admin/ProductGroupsPage';
import AddonsPage from '@pages/admin/AddonsPage';
import TLDPricingPage from '@pages/admin/TLDPricingPage';
import RegistrarsPage from '@pages/admin/RegistrarsPage';
import IntegrationsPage from '@pages/admin/IntegrationsPage';
import GeneralSettingsPage from '@pages/admin/GeneralSettingsPage';
import PaymentGatewaysPage from '@pages/admin/PaymentGatewaysPage';

// Affiliate Pages
import AffiliateDashboard from '@pages/affiliate/Dashboard';
import AffiliatePayouts from '@pages/affiliate/Payouts';
import AffiliateReports from '@pages/affiliate/Reports';
import AffiliateReportsAdmin from '@pages/admin/AffiliateReports';

// Public Pages
import PublicKnowledgeBasePage from '@pages/public/KnowledgeBase';
import PublicNetworkStatusPage from '@pages/public/NetworkStatus';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Public Knowledge Base */}
        <Route path="/knowledge-base" element={<PublicKnowledgeBasePage />} />
        <Route path="/knowledge-base/:slug" element={<PublicKnowledgeBasePage />} />

        {/* Public Network Status */}
        <Route path="/network-status" element={<PublicNetworkStatusPage />} />

        {/* Client Routes (Protected) */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDashboard />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/cart"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <CartPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/checkout"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <CheckoutPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders/:orderId/success"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <OrderSuccessPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders/:orderId"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientOrderDetailPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/orders"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientOrdersListPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/announcements"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientAnnouncementsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/downloads"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDownloadsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/services"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientServicesPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/invoices"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientInvoicesPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/tickets"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientTicketsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/domains"
          element={
            <ProtectedRoute>
              <ClientLayout>
                <ClientDomainsPage />
              </ClientLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes (Protected - Require Admin) */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Clients */}
        <Route
          path="/admin/clients/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientsListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Products */}
        <Route
          path="/admin/products"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ProductsListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Orders */}
        <Route
          path="/admin/orders/:orderId"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminOrderDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminOrdersListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Invoices */}
        <Route
          path="/admin/invoices"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminInvoicesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Services */}
        <Route
          path="/admin/services"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminServicesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Domains */}
        <Route
          path="/admin/domains/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDomainsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/domains"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDomainsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Tickets */}
        <Route
          path="/admin/tickets/:id"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ticket-departments"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminTicketDepartmentsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/canned-responses"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminCannedResponsesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Marketing */}
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminAnnouncementsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/promotions"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <PromotionsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/affiliates"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AffiliateReportsAdmin />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Content */}
        <Route
          path="/admin/knowledge-base"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminKnowledgeBasePage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/downloads"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminDownloadsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Analytics */}
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AnalyticsDashboardPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/financial-reports"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <FinancialReportsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/customer-analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <CustomerAnalyticsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* System */}
        <Route
          path="/admin/network-status"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminNetworkStatusPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/webhooks"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <WebhooksPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AdminEmailTemplatesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Client Groups */}
        <Route
          path="/admin/client-groups"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ClientGroupsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Product Groups */}
        <Route
          path="/admin/product-groups"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <ProductGroupsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Addons */}
        <Route
          path="/admin/addons"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <AddonsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* TLD Pricing */}
        <Route
          path="/admin/tld-pricing"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <TLDPricingPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Registrars */}
        <Route
          path="/admin/registrars"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <RegistrarsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Emails redirect to Email Templates */}
        <Route
          path="/admin/emails"
          element={<Navigate to="/admin/email-templates" replace />}
        />

        {/* Integrations */}
        <Route
          path="/admin/integrations"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <IntegrationsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="/admin/settings/general"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <GeneralSettingsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/payment-gateways"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <PaymentGatewaysPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/languages"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <LanguagesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/currencies"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <CurrenciesPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/tax"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout>
                <TaxSettingsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Affiliate Portal */}
        <Route
          path="/affiliate/dashboard"
          element={
            <ProtectedRoute>
              <AffiliateDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/payouts"
          element={
            <ProtectedRoute>
              <AffiliatePayouts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate/reports"
          element={
            <ProtectedRoute>
              <AffiliateReports />
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
              <div className="text-center animate-fade-in">
                <h1 className="text-8xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent font-display">404</h1>
                <p className="mt-4 text-xl text-slate-400">Page not found</p>
                <p className="mt-2 text-sm text-slate-500">The page you're looking for doesn't exist or has been moved.</p>
                <a href="/" className="mt-6 inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 font-medium">
                  Go Home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
